// src/components/vault/ui/MobileStepSheet.tsx
import React from "react";
import { Check, Loader2, Lock } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Requirement, Status } from "../../../redux/interfaces/folder.types";
import { useBreakpoint } from "../../../hooks/useIsMobile";
import { ScrollArea } from "../../ui/scroll-area";
import { FolderStatusBadge } from "./FolderStatusBadge";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "../../ui/drawer";
import {
  getRequirementProcessingLabel,
  getRequirementGroup,
  getVaultRequirementLockedDescription,
  isBdcAuthorizationRequirement,
  isRequirementProcessing,
  isRequirementLockedByPrerequisites,
  isRequirementVisuallyComplete,
  isIdentityRequirement,
} from "../utils/requirementGrouping";
import {
  getIdentityValidationActivityLabel,
  isIdentityValidationActivityProcessing,
  type IdentityValidationActivity,
} from "../identityValidationActivity";

// ============================================================================
// Types
// ============================================================================

interface MobileStepSheetProps {
  title: string;
  description?: string;
  folioNumber?: string;
  tenantName?: string;
  actorType?: string;
  folderStatus?: string;
  logo?: React.ReactNode;
  requirements: Requirement[];
  activeRequirement: string | null;
  onSelectRequirement: (requirementId: string) => void;
  isIDCompleted?: boolean;
  isProofOfAddressCompleted?: boolean;
  isDataValidationCompleted?: boolean;
  isBdcDestinationReviewed?: boolean;
  existingBdcRunRequirementId?: string | null;
  legacyCompletedBdcRequirementId?: string | null;
  bdcReportReplacementRequirementId?: string | null;
  bdcAuthoritativeRequirementStatus?: string;
  isAddressConfirmedUpdated?: boolean;
  identityValidationActivity?: IdentityValidationActivity;
  lockedFormSlugs?: string[] | null;
  bdcPrerequisiteSlugs?: string[] | null;
  // Mobile-specific
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// Locked-state descriptions (matching VaultSidebar convention)
// ============================================================================

const requirementDescriptions: Record<string, string> = {
  "official-id": "Documento de identidad vigente",
  "identificacion-oficial": "Documento de identidad vigente",
  "data-validation": "Confirma los datos extraidos de tus documentos",
  "proof-of-income": "Comprobantes de ingresos recientes",
  "comprobante-ingresos": "Comprobantes de ingresos recientes",
  "proof-of-address": "Comprobante de domicilio reciente",
  "comprobante-domicilio": "Comprobante de domicilio reciente",
  "personal-reference-1": "Datos de contacto de referencia",
  "personal-reference-2": "Datos de contacto de referencia",
  "referencia-personal-1": "Datos de contacto de referencia",
  "referencia-personal-2": "Datos de contacto de referencia",
  "commercial-reference-1": "Datos de referencia comercial",
  "commercial-reference-2": "Datos de referencia comercial",
  "referencia-comercial-1": "Datos de referencia comercial",
  "referencia-comercial-2": "Datos de referencia comercial",
  "digital-signature": "Firma electrónica del documento",
  "firma-digital": "Firma electrónica del documento",
  "bdc-authorization": "Autorización de consulta crediticia",
  "autorizacion-bdc": "Autorización de consulta crediticia",
  "bdc-company-data": "Datos de la empresa para consulta crediticia",
  "tenant-form": "Datos personales del inquilino",
  "formulario-inquilino": "Datos personales del inquilino",
  "formulario-del-inquilino": "Datos personales del inquilino",
  "guarantor-form": "Datos del obligado solidario",
  "formulario-fiador": "Datos del obligado solidario",
  "formulario-obligado-solidario": "Datos del obligado solidario",
  "landlord-form": "Datos personales del propietario",
  "formulario-propietario": "Datos personales del propietario",
  "owner-representative-form": "Datos del representante",
  "formulario-representante-propietario": "Datos del representante",
  "business-form": "Datos de la empresa",
  "formulario-empresa": "Datos de la empresa",
  "business-cja-form": "Datos de la empresa",
  "business-short-form": "Datos básicos de la empresa",
  "business-form-lease-contract": "Datos de la empresa arrendadora",
  "legal-rep-form": "Datos del representante legal",
  "formulario-representante-legal": "Datos del representante legal",
  "legal-rep-short-form": "Datos del representante legal",
  "constitutive-act": "Acta constitutiva de la empresa",
  "acta-constitutiva": "Acta constitutiva de la empresa",
  "sat-compliance": "Opinión de cumplimiento fiscal",
  "opinion-sat": "Opinión de cumplimiento fiscal",
  "power-of-attorney": "Poder notarial vigente",
  "poder-legal": "Poder notarial vigente",
  predial: "Recibo de pago del predial vigente",
  "boleta-registral": "Boleta registral del inmueble",
  "escrituras-predial": "Escrituras o predial del inmueble",
  escrituras: "Escrituras del inmueble",
  "lease-contract-form": "Datos de la renta y contrato",
  "formulario-contrato": "Datos de la renta y contrato",
};

// ============================================================================
// Component
// ============================================================================

export function MobileStepSheet({
  requirements,
  folioNumber,
  tenantName,
  folderStatus,
  logo,
  activeRequirement,
  onSelectRequirement,
  isIDCompleted = true,
  isProofOfAddressCompleted = false,
  isDataValidationCompleted = false,
  isBdcDestinationReviewed = true,
  existingBdcRunRequirementId = null,
  legacyCompletedBdcRequirementId = null,
  bdcReportReplacementRequirementId = null,
  bdcAuthoritativeRequirementStatus,
  isAddressConfirmedUpdated = false,
  identityValidationActivity = null,
  lockedFormSlugs = null,
  bdcPrerequisiteSlugs = null,
  isOpen,
  onClose,
}: MobileStepSheetProps) {
  const { isDesktop } = useBreakpoint();

  // Desktop: never render
  if (isDesktop) return null;

  // Progress
  const completedCount = requirements.filter(
    (requirement) =>
      !(identityValidationActivity && isIdentityRequirement(requirement)) &&
      isRequirementVisuallyComplete(requirement),
  ).length;
  const progressPercentage =
    requirements.length > 0
      ? Math.round((completedCount / requirements.length) * 100)
      : 0;

  // Requirement groups
  const hasBdcAuthorization = requirements.some((requirement) =>
    isBdcAuthorizationRequirement(requirement.slug, requirement.type),
  );
  const identityRequirements = requirements.filter(
    (r) =>
      getRequirementGroup(
        r.slug,
        r.type,
        lockedFormSlugs,
        hasBdcAuthorization,
      ) === "identity",
  );
  const additionalRequirements = requirements.filter(
    (r) =>
      getRequirementGroup(
        r.slug,
        r.type,
        lockedFormSlugs,
        hasBdcAuthorization,
      ) === "additional",
  );
  const hasIdentityGroup = identityRequirements.length > 0;
  const hasAdditionalGroup = additionalRequirements.length > 0;
  const hasBothGroups = hasIdentityGroup && hasAdditionalGroup;

  const lockContext = {
    requirements,
    isIDCompleted,
    isProofOfAddressCompleted,
    isDataValidationCompleted,
    isBdcDestinationReviewed,
    existingBdcRunRequirementId,
    legacyCompletedBdcRequirementId,
    bdcReportReplacementRequirementId,
    bdcAuthoritativeRequirementStatus,
    isAddressConfirmedUpdated,
    lockedFormSlugs,
    bdcPrerequisiteSlugs,
  };
  const isRequirementLocked = (requirement: Requirement): boolean =>
    isRequirementLockedByPrerequisites(requirement, lockContext);

  const handleSelect = (requirementId: string) => {
    onSelectRequirement(requirementId);
    onClose();
  };

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="max-h-[85vh] pb-[env(safe-area-inset-bottom,0px)]">
        {/* Header */}
        <DrawerHeader className="text-left">
          {logo && (
            <div className="pb-2 [&_img]:max-h-[55px] [&_img]:w-auto">
              {logo}
            </div>
          )}
          {folioNumber && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-[#28397a] font-semibold uppercase tracking-wide">
                Folio #{folioNumber}
              </span>
              <FolderStatusBadge status={folderStatus} />
            </div>
          )}
          {tenantName && <DrawerTitle>{tenantName}</DrawerTitle>}
        </DrawerHeader>

        {/* Progress bar */}
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="text-slate-500">Progreso</span>
            <span className="font-medium text-slate-700">
              {completedCount} de {requirements.length}
            </span>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#28397a] rounded-full transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Scrollable requirements list */}
        <ScrollArea className="flex-1 overflow-y-auto">
          <div className="px-4 pb-6">
            {/* Identity group */}
            {hasIdentityGroup && (
              <div>
                {hasBothGroups && (
                  <div className="pb-2 pt-1">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                      Verificación de identidad
                    </span>
                  </div>
                )}
                {identityRequirements.map((requirement, idx) => (
                  <MobileRequirementItem
                    key={requirement.id}
                    requirement={requirement}
                    stepNumber={idx + 1}
                    isActive={activeRequirement === requirement.id}
                    isLocked={isRequirementLocked(requirement)}
                    lockedDescription={getVaultRequirementLockedDescription(
                      requirement,
                      lockContext,
                    )}
                    identityValidationActivity={identityValidationActivity}
                    onSelect={handleSelect}
                  />
                ))}
              </div>
            )}

            {/* Separator */}
            {hasBothGroups && (
              <div className="my-2 border-t border-slate-100" />
            )}

            {/* Additional group */}
            {hasAdditionalGroup && (
              <div>
                {hasBothGroups && (
                  <div className="pb-2 pt-1">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                      Documentos adicionales
                    </span>
                  </div>
                )}
                {additionalRequirements.map((requirement, idx) => (
                  <MobileRequirementItem
                    key={requirement.id}
                    requirement={requirement}
                    stepNumber={idx + 1}
                    isActive={activeRequirement === requirement.id}
                    isLocked={isRequirementLocked(requirement)}
                    lockedDescription={getVaultRequirementLockedDescription(
                      requirement,
                      lockContext,
                    )}
                    identityValidationActivity={identityValidationActivity}
                    onSelect={handleSelect}
                  />
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </DrawerContent>
    </Drawer>
  );
}

// ============================================================================
// Mobile Requirement Item
// ============================================================================

interface MobileRequirementItemProps {
  requirement: Requirement;
  stepNumber: number;
  isActive: boolean;
  isLocked: boolean;
  lockedDescription: string;
  identityValidationActivity?: IdentityValidationActivity;
  onSelect: (requirementId: string) => void;
}

function MobileRequirementItem({
  requirement,
  stepNumber,
  isActive,
  isLocked,
  lockedDescription,
  identityValidationActivity = null,
  onSelect,
}: MobileRequirementItemProps) {
  const localIdentityActivity = isIdentityRequirement(requirement)
    ? identityValidationActivity
    : null;
  const isCompleted =
    localIdentityActivity === null &&
    isRequirementVisuallyComplete(requirement);
  const isProcessing =
    localIdentityActivity !== null
      ? isIdentityValidationActivityProcessing(localIdentityActivity)
      : isRequirementProcessing(requirement);
  const processingLabel =
    getIdentityValidationActivityLabel(localIdentityActivity) ??
    getRequirementProcessingLabel(requirement);
  const isInvalid = requirement.status === Status.Invalid;

  const description =
    requirementDescriptions[requirement.slug] ||
    requirementDescriptions[requirement.type] ||
    null;

  return (
    <button
      onClick={() => !isLocked && onSelect(requirement.id)}
      disabled={isLocked}
      className={cn(
        "w-full flex items-center gap-3 min-h-[56px] px-2 py-2.5 rounded-lg text-left transition-colors active:bg-slate-50",
        isLocked && "pointer-events-none opacity-50",
        isActive && "bg-[#28397a]/5",
      )}
    >
      {/* Step circle */}
      <div className="flex-shrink-0 relative">
        {isProcessing ? (
          <div
            className="w-9 h-9 rounded-full bg-[#28397a] flex items-center justify-center"
            role="status"
            aria-label={processingLabel ?? "Procesando requisito"}
          >
            <span className="inline-flex animate-spin">
              <Loader2 className="w-4 h-4 text-white" strokeWidth={2.5} />
            </span>
          </div>
        ) : isCompleted ? (
          <div className="w-9 h-9 rounded-full bg-[#28397a] flex items-center justify-center">
            <Check className="w-4 h-4 text-white" strokeWidth={2.5} />
            {isLocked && (
              <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-white">
                <Lock className="h-2.5 w-2.5 text-slate-500" />
              </span>
            )}
          </div>
        ) : isLocked ? (
          <div className="w-9 h-9 rounded-full bg-slate-100 border-2 border-slate-200 flex items-center justify-center">
            <Lock className="w-4 h-4 text-slate-400" strokeWidth={2} />
          </div>
        ) : isActive ? (
          <div className="w-9 h-9 rounded-full bg-[#28397a] flex items-center justify-center">
            <span className="text-sm font-semibold text-white">
              {stepNumber}
            </span>
          </div>
        ) : isInvalid ? (
          <div className="w-9 h-9 rounded-full bg-red-50 border-2 border-red-200 flex items-center justify-center">
            <span className="text-sm font-medium text-red-500">
              {stepNumber}
            </span>
          </div>
        ) : (
          <div className="w-9 h-9 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center">
            <span className="text-sm font-medium text-slate-400">
              {stepNumber}
            </span>
          </div>
        )}
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <span
          className={cn(
            "text-sm leading-tight block",
            isLocked
              ? "text-slate-400"
              : isActive || isProcessing
                ? "text-[#28397a] font-semibold"
                : isCompleted
                  ? "text-slate-700"
                  : isInvalid
                    ? "text-red-700"
                    : "text-slate-600",
          )}
        >
          {requirement.name}
        </span>

        {isProcessing ? (
          <span className="text-xs text-[#28397a]/70 mt-0.5 block">
            {processingLabel}
          </span>
        ) : isLocked ? (
          <span className="text-xs text-slate-400 mt-0.5 block">
            {lockedDescription}
          </span>
        ) : isInvalid ? (
          <span className="text-xs text-red-500 mt-0.5 block">
            Requiere corrección
          </span>
        ) : description ? (
          <span className="text-xs text-slate-400 mt-0.5 block">
            {description}
          </span>
        ) : null}
      </div>

      {/* Active indicator chevron */}
      {isActive && !isLocked && (
        <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#28397a]" />
      )}
    </button>
  );
}
